import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import ReportDataMenu from '../../components/ReportDataMenu';

// ── Seat Occupancy Report ─────────────────────────────────────────────────────
// How long each desk was actually occupied on a given day, from the seat_occupancy
// ledger. Three numbers per desk, in the order a manager reads them:
//   time at desk   — integrated occupied time, short breaks already excluded
//   longest gap    — the single longest stretch the desk sat empty in-hours
//   day timeline   — when those stretches were, so a total can be checked at a glance
//
// Time at desk is NOT last-seen minus first-seen: a break the detector absorbed to
// keep one session is reported separately as "away", never as work.

const fmt = (s) => {
  s = Math.max(0, Math.round(s || 0));
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};
const clock = (ts) => ts ? new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function IndustrialSeatReportPage() {
  const [date, setDate]       = useState(todayStr());
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState('');       // '' = every camera with seats
  const [rep, setRep]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSeat, setOpenSeat] = useState(null);   // seat whose sessions are expanded
  const [live, setLive]       = useState(null);     // real-time read, today only
  const nav = useNavigate();

  const isToday = date === todayStr();

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => setCameras(d.cameras || [])).catch(() => {});
  }, []);

  // Cameras that actually have seats drawn — the only ones worth offering as a filter.
  const seatCams = useMemo(() => cameras
    .filter(c => (c.detector_config?.seat_occupancy?.seats || []).length)
    .map(c => ({ idx: c.idx, name: c.name || `Camera ${c.idx}` })), [cameras]);

  const load = useCallback(() => {
    const q = new URLSearchParams({ date: date.replace(/-/g, '') });
    if (scope !== '') q.set('cam', scope);
    apiFetch(`/ui/seat/report?${q}`).then(r => r.json())
      .then(d => { setRep(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [date, scope]);

  useEffect(() => {
    setLoading(true); load();
    if (!isToday) return;                       // only a live day needs refreshing
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, isToday]);

  // Real-time occupancy, straight off the running detectors. The report itself is
  // ledger-derived, so it only learns a desk was vacated once the merge gap elapses
  // and a 'leave' is written — minutes late. This poll is what makes the top band
  // say what is true right now; it is dropped entirely on a past day.
  useEffect(() => {
    if (!isToday) { setLive(null); return; }
    let alive = true;
    const tick = () => apiFetch('/ui/seat/live').then(r => r.json())
      .then(d => { if (alive) setLive(d); }).catch(() => {});
    tick(); const t = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [isToday]);

  // Flatten to one row per seat, honouring the camera filter. Empty when no worker
  // with seats is running (service restarting, or seats saved but not yet applied) —
  // in which case the band hides and the ledger figure stands.
  const liveSeats = useMemo(() => {
    const cams = (live?.cameras || []).filter(c => scope === '' || String(c.cam_idx) === String(scope));
    return cams.flatMap(c => (c.seats || []).map(s => ({ ...s, cam_idx: c.cam_idx, cam_name: c.cam_name })));
  }, [live, scope]);

  // How long since the detector last looked. A camera whose worker has died keeps
  // returning its last state for ever, so the band has to say when it went quiet
  // rather than showing a stale count as if it were current.
  const staleSecs = useMemo(() => {
    const cams = (live?.cameras || []).filter(c => scope === '' || String(c.cam_idx) === String(scope));
    if (!cams.length) return null;
    const worst = cams.reduce((m, c) => Math.max(m, c.sampled_ago ?? 0), 0);
    const cadence = Math.max(...cams.map(c => c.sample_secs || 10));
    return worst > cadence * 3 ? worst : 0;
  }, [live, scope]);

  const seats = useMemo(() => rep?.seats || [], [rep]);
  const dayStart = rep?.day_start || 0;
  // The timeline spans working hours when configured, else the day's own activity.
  const [tlFrom, tlTo] = useMemo(() => {
    if (rep?.work_start && rep?.work_end) return [rep.work_start, rep.work_end];
    if (!seats.length) return [dayStart + 8 * 3600, dayStart + 20 * 3600];
    return [Math.min(...seats.map(s => s.first_seen)), Math.max(...seats.map(s => s.last_seen))];
  }, [rep, seats, dayStart]);
  const tlSpan = Math.max(1, tlTo - tlFrom);
  const pct = (ts) => Math.max(0, Math.min(100, ((ts - tlFrom) / tlSpan) * 100));

  const totals = useMemo(() => ({
    // Prefer the real-time read; fall back to the ledger when no detector is running.
    manned: liveSeats.length ? liveSeats.filter(s => s.occupied).length
                             : seats.filter(s => s.occupied_now).length,
    avg: seats.length ? seats.reduce((a, s) => a + (s.work_secs || s.total_secs), 0) / seats.length : 0,
  }), [seats, liveSeats]);

  const shiftDay = (n) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + n);
    if (d > new Date()) return;
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  if (!loading && !seats.length && !seatCams.length) {
    return (
      <IndustrialLayout eyebrow="Seats" title="Seat Occupancy" subtitle="NO SEATS CONFIGURED">
        <div className="ind-card">
          <div className="ind-empty" style={{ padding: 28, lineHeight: 1.8 }}>
            <b>No seats are being measured yet.</b><br />
            Open <b>Seat Zones</b>, pick the office camera, and draw a polygon around each
            person’s seat — the time at each desk is recorded from then on.<br />
            <button className="ind-pill on" style={{ marginTop: 14 }}
              onClick={() => nav('/industrial/seats')}>💺 Set up seat zones</button>
          </div>
        </div>
      </IndustrialLayout>
    );
  }

  return (
    <IndustrialLayout eyebrow="Seats" title="Seat Occupancy"
      subtitle={`${rep?.date || date} · ${seats.length} SEAT${seats.length === 1 ? '' : 'S'}${isToday ? ` · ${totals.manned} OCCUPIED NOW` : ''}`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span className="ind-eye">Day</span>
          <button className="ind-pill" onClick={() => shiftDay(-1)}>‹</button>
          <input type="date" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} style={sel} />
          <button className="ind-pill" onClick={() => shiftDay(1)} disabled={isToday}>›</button>
          {!isToday && <button className="ind-pill" onClick={() => setDate(todayStr())}>Today</button>}
          {seatCams.length > 1 && (
            <select value={scope} onChange={e => setScope(e.target.value)} style={{ ...sel, marginLeft: 12 }}>
              <option value="">All cameras</option>
              {seatCams.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name}</option>)}
            </select>
          )}
          <span style={{ flex: 1 }} />
          {(() => {
            // Data is reset per camera — the one picked, or the only seat camera there is.
            const c = scope !== '' ? seatCams.find(x => String(x.idx) === String(scope))
                                   : (seatCams.length === 1 ? seatCams[0] : null);
            if (!c) return null;
            const its = ((cameras.find(x => x.idx === c.idx)?.detector_config?.seat_occupancy?.seats) || [])
              .map(s => s.name).filter(Boolean);
            return <ReportDataMenu kind="seat" cam={c.idx} camName={c.name} items={its} onChanged={load} />;
          })()}
          {rep?.work_start && (
            <span style={{ fontSize: 11, color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)' }}>
              SHIFT {clock(rep.work_start)}–{clock(rep.work_end)}
            </span>
          )}
        </div>

        {/* ── LIVE NOW ─────────────────────────────────────────────────────────
            Today only, straight off the running detectors — refreshes every 5s.
            The rest of the page is the day's history; this band is the present. */}
        {isToday && liveSeats.length > 0 && (
          <div style={{ border: '1px solid var(--ind-line)', borderRadius: 10, padding: 14,
            marginBottom: 18, background: 'var(--ind-ink)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span className="ind-eye">Live now</span>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: staleSecs ? 'var(--ind-tx3)' : 'var(--ind-ok)',
                display: 'inline-block', alignSelf: 'center' }} />
              <span style={{ fontSize: 11, color: 'var(--ind-tx3)' }}>
                {staleSecs
                  ? `No check for ${fmt(staleSecs)} — the camera worker may have stopped.`
                  : 'Updating every 5 seconds'}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 26, fontWeight: 800, fontFamily: 'var(--ind-mono)',
                color: totals.manned ? 'var(--ind-ok)' : 'var(--ind-tx3)', lineHeight: 1 }}>
                {totals.manned}
              </span>
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                of {liveSeats.length} desk{liveSeats.length === 1 ? '' : 's'} occupied
              </span>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {liveSeats.map(s => (
                <div key={`${s.cam_idx}:${s.seat}`}
                  title={s.occupied ? `At the desk — ${fmt(s.secs)} in this stretch` : 'Desk empty right now'}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 11px', borderRadius: 8,
                    border: `1px solid ${s.occupied ? 'var(--ind-ok)' : 'var(--ind-line)'}`,
                    background: s.occupied ? 'rgba(34,197,94,0.10)' : 'transparent',
                    opacity: staleSecs ? 0.5 : 1 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 99, flexShrink: 0,
                    background: s.occupied ? 'var(--ind-ok)' : 'var(--ind-line)' }} />
                  <span style={{ fontSize: 12, color: s.occupied ? 'var(--ind-tx)' : 'var(--ind-tx3)' }}>{s.seat}</span>
                  <span style={{ fontSize: 11, fontFamily: 'var(--ind-mono)',
                    color: s.occupied ? 'var(--ind-ok)' : 'var(--ind-tx3)' }}>
                    {s.occupied ? fmt(s.secs) : 'empty'}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 10.5, color: 'var(--ind-tx3)', margin: '10px 0 0', lineHeight: 1.6 }}>
              The time on an occupied desk is the <b>current stretch</b>, not the day’s total — that is
              the figure in the table below.
            </p>
          </div>
        )}

        {loading && <div className="ind-empty" style={{ padding: 30 }}>Loading…</div>}

        {!loading && !seats.length && (
          <div className="ind-empty" style={{ padding: 30, lineHeight: 1.7 }}>
            Nothing recorded on {rep?.date || date}. Either nobody sat at a measured desk,
            or the seats were configured after this day.
          </div>
        )}

        {!loading && seats.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
              <Stat label="SEATS" val={seats.length} />
              {/* Don't repeat the live band's own headline count right underneath it. */}
              {isToday && liveSeats.length > 0
                ? <Stat label="SEATS USED TODAY" val={seats.filter(s => s.total_secs > 0).length} color="var(--ind-ok)" />
                : <Stat label={isToday ? 'OCCUPIED NOW' : 'SEATS USED'}
                    val={isToday ? totals.manned : seats.filter(s => s.total_secs > 0).length}
                    color="var(--ind-ok)" />}
              <Stat label="AVG TIME AT DESK" val={fmt(totals.avg)} small />
              {rep?.seats?.[0]?.expected_secs
                ? <Stat label="SHIFT LENGTH" val={fmt(rep.seats[0].expected_secs)} small />
                : null}
            </div>

            {/* Timeline scale */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10,
              color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)', marginBottom: 4, paddingLeft: 230 }}>
              <span>{clock(tlFrom)}</span><span>{clock(tlFrom + tlSpan / 2)}</span><span>{clock(tlTo)}</span>
            </div>

            <div style={{ border: '1px solid var(--ind-line)', borderRadius: 8, overflow: 'hidden' }}>
              {seats.map((s, i) => {
                const key = `${s.cam_idx}:${s.seat}`;
                const shown = s.work_secs || s.total_secs;
                const ratio = s.expected_secs ? shown / s.expected_secs : null;
                const open = openSeat === key;
                return (
                  <div key={key} style={{ borderTop: i ? '1px solid var(--ind-line)' : 'none' }}>
                    <div onClick={() => setOpenSeat(open ? null : key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }}>
                      {/* name + total */}
                      <div style={{ width: 220, flexShrink: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--ind-tx)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {s.occupied_now && <span title="At the desk right now"
                            style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--ind-ok)', display: 'inline-block' }} />}
                          {s.seat}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ind-tx3)' }}>
                          {s.cam_name} · {clock(s.first_seen)}–{s.occupied_now ? 'now' : clock(s.last_seen)}
                        </div>
                      </div>

                      {/* timeline */}
                      <div style={{ flex: 1, position: 'relative', height: 22, background: 'var(--ind-ink)',
                        border: '1px solid var(--ind-line)', borderRadius: 5, overflow: 'hidden' }}>
                        {s.sessions.map((ss, j) => (
                          <div key={j} title={`${clock(ss.start)}–${clock(ss.end)} · ${fmt(ss.secs)}`}
                            style={{ position: 'absolute', top: 0, bottom: 0,
                              left: `${pct(ss.start)}%`,
                              width: `${Math.max(0.6, pct(ss.end) - pct(ss.start))}%`,
                              background: ss.open ? 'var(--ind-ok)' : 'rgba(34,197,94,0.62)' }} />
                        ))}
                      </div>

                      {/* figures */}
                      <div style={{ width: 92, textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--ind-mono)',
                          color: ratio != null && ratio < 0.6 ? 'var(--ind-accent)' : 'var(--ind-tx)' }}>
                          {fmt(shown)}
                        </div>
                        <div style={{ fontSize: 9.5, color: 'var(--ind-tx3)' }}>
                          {ratio != null ? `${Math.round(ratio * 100)}% of shift` : 'at desk'}
                        </div>
                      </div>
                      <div style={{ width: 92, textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontFamily: 'var(--ind-mono)', color: 'var(--ind-tx2)' }}>
                          {fmt(s.longest_gap_secs)}
                        </div>
                        <div style={{ fontSize: 9.5, color: 'var(--ind-tx3)' }}>
                          longest gap{s.longest_gap_at ? ` ${clock(s.longest_gap_at)}` : ''}
                        </div>
                      </div>
                      <span style={{ color: 'var(--ind-tx3)', fontSize: 11, width: 12 }}>{open ? '▾' : '▸'}</span>
                    </div>

                    {open && (
                      <div style={{ padding: '0 12px 12px 232px', fontSize: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)', fontSize: 10.5 }}>
                            <th style={th}>Sat down</th><th style={th}>Left</th>
                            <th style={th}>At desk</th><th style={th}>Short breaks</th>
                          </tr></thead>
                          <tbody>
                            {s.sessions.map((ss, j) => (
                              <tr key={j} style={{ borderTop: '1px solid var(--ind-line)' }}>
                                <td style={td}>{clock(ss.start)}</td>
                                <td style={td}>{ss.open ? <span style={{ color: 'var(--ind-ok)' }}>still there</span> : clock(ss.end)}</td>
                                <td style={td}>{fmt(ss.secs)}</td>
                                <td style={{ ...td, color: 'var(--ind-tx3)' }}>{ss.away_secs ? fmt(ss.away_secs) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {s.away_secs > 0 && (
                          <p style={{ fontSize: 10.5, color: 'var(--ind-tx3)', marginTop: 6 }}>
                            {fmt(s.away_secs)} of short breaks were kept inside these sessions so the timeline
                            stays readable — that time is <b>not</b> counted as time at desk.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 14, lineHeight: 1.7, maxWidth: 820 }}>
              The desk is checked every few seconds; a person must be seen for two checks in a row before
              the clock starts, so walking past a desk never registers. Time at desk excludes short
              breaks. A desk can only be measured while the person is visible to the camera — someone
              working from a meeting room reads as away, which is why the gap column matters more than
              the total.
              {' '}<button className="ind-pill" style={{ padding: '2px 8px' }}
                onClick={() => nav('/industrial/seats')}>Edit seat zones</button>
            </p>
          </>
        )}
      </div>
    </IndustrialLayout>
  );
}

function Stat({ label, val, color, small }) {
  return (
    <div style={{ flex: '1 1 110px', padding: '14px 12px', borderRadius: 10, textAlign: 'center',
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: small ? 20 : 30, fontWeight: 700, fontFamily: 'var(--ind-mono)',
        lineHeight: 1.2, color: color || 'var(--ind-tx)' }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const th  = { padding: '5px 10px', fontWeight: 600 };
const td  = { padding: '5px 10px', fontFamily: 'var(--ind-mono)' };
